import type {ESignerFieldData} from '@/types/esign';

/**
 * Pre-save sanity checks for e-sign templates. These are warnings, not hard
 * errors — the user can always save anyway — but each one describes a template
 * that would produce a broken or confusing envelope for the customer.
 *
 * Signer conventions (mirroring the tool palette): a field belongs to the
 * secondary signer when `secondary_customer` is set; everything else is
 * effectively the primary signer's.
 */
export function validateTemplateFields(fields: ESignerFieldData[]): string[] {
    const warnings: string[] = [];

    if (fields.length === 0) {
        warnings.push('The template has no fields. Customers will have nothing to fill in or sign.');
        return warnings;
    }

    const signatures = fields.filter((field) => field.type === 'signature');
    const hasPrimarySignature = signatures.some((field) => !field.secondary_customer);
    if (!hasPrimarySignature) {
        warnings.push('There is no signature field for the primary signer, so the agreement cannot be signed.');
    }

    const hasSecondaryFields = fields.some((field) => field.secondary_customer);
    const hasSecondarySignature = signatures.some((field) => field.secondary_customer);
    if (hasSecondaryFields && !hasSecondarySignature) {
        warnings.push('The secondary signer has fields to fill in but no signature field.');
    }

    const outOfBounds = fields.filter((field) => field.x < 0 || field.y < 0 || field.w < 0 || field.h < 0);
    if (outOfBounds.length > 0) {
        const label = outOfBounds.length === 1 ? '1 field is' : `${outOfBounds.length} fields are`;
        warnings.push(`${label} positioned outside the page and may not render correctly.`);
    }

    return warnings;
}
